import { useCallback, useRef, useState, type ReactNode } from "react";
import { useNotify, type Identifier } from "ra-core";
import { useFormContext } from "react-hook-form";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import { avisarGestorDuplicidade, donoDoCelular, type OrigemDoAviso } from "./celularUnico";
import {
  MENSAGEM_CELULAR_NA_SUA_BASE,
  erroDeCelular,
  mensagemCelularDeOutro,
  normalizarCelular,
} from "./gravarContato";

/**
 * Celular já cadastrado na base de outra pessoa (pedido de 08/10/2026): a aba
 * Contatos não grava e oferece "Avisar o gestor" — que abre a ocorrência em
 * Duplicidades para um super admin decidir a transferência. "Fechar" não grava
 * nada. Nos outros caminhos (novo lead, seguros, importação) basta a mensagem
 * do banco (gravarContato.ts).
 */

export interface AvisoDeCelular {
  dono: string;
  telefone: string;
  nome: string;
}

export const AvisoCelularDuplicado = ({
  aviso,
  origem = "cadastro",
  aoFechar,
}: {
  aviso: AvisoDeCelular | null | undefined;
  origem?: OrigemDoAviso;
  aoFechar?: () => void;
}) => {
  const notify = useNotify();
  const [enviando, setEnviando] = useState(false);
  const [avisado, setAvisado] = useState(false);

  const fechar = () => {
    setAvisado(false);
    aoFechar?.();
  };

  const avisar = async () => {
    if (!aviso) return;
    setEnviando(true);
    try {
      await avisarGestorDuplicidade(aviso.telefone, aviso.nome, origem);
      setAvisado(true);
    } catch (e) {
      notify(e instanceof Error ? e.message : "Não foi possível avisar o gestor.", { type: "error" });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Dialog open={!!aviso} onOpenChange={(aberto) => !aberto && fechar()}>
      <DialogContent className="z-[70] sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Celular já cadastrado</DialogTitle>
          <DialogDescription>{aviso ? mensagemCelularDeOutro(aviso.dono) : ""}</DialogDescription>
        </DialogHeader>
        {avisado ? (
          <p role="status" className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
            Gestor avisado.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            Contato não salvo. A transferência para a sua base é solicitada ao gestor e autorizada por um
            administrador.
          </p>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={fechar}>
            Fechar
          </Button>
          {!avisado && (
            <Button type="button" onClick={() => void avisar()} disabled={enviando}>
              {enviando ? "Avisando..." : "Avisar o gestor"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * O modal e o jeito de abri-lo, para o cadastro e a edição. `aoErro` cobre o
 * caso em que a conferência antes de salvar não pegou (Enter no campo, ou
 * alguém gravou o número no meio): o gatilho da 240 recusa e o modal abre do
 * mesmo jeito. Devolve true quando tratou o erro.
 */
export const useCelularDuplicado = (origem: OrigemDoAviso) => {
  const [aviso, setAviso] = useState<AvisoDeCelular | null>(null);
  const enviado = useRef<{ telefone: string; nome: string }>({ telefone: "", nome: "" });

  const lembrar = useCallback((details: Record<string, unknown> | undefined) => {
    enviado.current = { telefone: String(details?.phone ?? ""), nome: String(details?.name ?? "") };
  }, []);

  const aoErro = useCallback((error: unknown) => {
    const celular = erroDeCelular(error);
    if (celular?.tipo !== "outro") return false;
    setAviso({ dono: celular.dono, ...enviado.current });
    return true;
  }, []);

  const modal = <AvisoCelularDuplicado aviso={aviso} origem={origem} aoFechar={() => setAviso(null)} />;
  return { abrir: setAviso, aoErro, lembrar, modal };
};

/**
 * Envolve o botão de salvar: antes de gravar, pergunta ao banco de quem é o
 * celular. Livre, deixa o clique seguir; na base de outro, abre o modal; na
 * própria base, avisa e não grava. Na edição, número igual ao de antes não é
 * conferido (duplicado antigo continua editável, como no banco).
 */
export const ConferirCelularAoSalvar = ({
  children,
  contatoId,
  celularAtual,
  aoDuplicadoDeOutro,
}: {
  children: ReactNode;
  contatoId?: Identifier | null;
  celularAtual?: string | null;
  aoDuplicadoDeOutro?: (aviso: AvisoDeCelular) => void;
}) => {
  const notify = useNotify();
  const { getValues } = useFormContext();
  const caixa = useRef<HTMLSpanElement>(null);
  const liberado = useRef(false);
  const [conferindo, setConferindo] = useState(false);

  const seguir = () => {
    liberado.current = true;
    caixa.current?.querySelector<HTMLButtonElement>("button[type=submit], button")?.click();
  };

  const aoClicar = (e: React.MouseEvent) => {
    if (liberado.current) {
      liberado.current = false;
      return;
    }
    // Só o botão de salvar: o React sobe eventos de portais (o diálogo de troca
    // de responsável do SalvarComResponsavel) até aqui, e esses passam direto.
    const alvo = (e.target as HTMLElement).closest("button");
    if (!alvo || !caixa.current?.contains(alvo) || conferindo) return;
    const telefone = String(getValues("details.phone") ?? "");
    const numero = normalizarCelular(telefone);
    // Sem número, o campo obrigatório avisa; número que não mudou não é conferido.
    if (!numero || (celularAtual != null && numero === normalizarCelular(celularAtual))) return;

    e.preventDefault();
    e.stopPropagation();
    setConferindo(true);
    donoDoCelular(telefone, contatoId)
      .then((r) => {
        if (r.situacao === "livre") return seguir();
        if (r.situacao === "propria") return void notify(MENSAGEM_CELULAR_NA_SUA_BASE, { type: "error" });
        aoDuplicadoDeOutro?.({ dono: r.dono_nome, telefone, nome: String(getValues("details.name") ?? "") });
      })
      // Sem resposta do banco, quem decide é o gatilho ao gravar.
      .catch(seguir)
      .finally(() => setConferindo(false));
  };

  return (
    <span ref={caixa} className="contents" onClickCapture={aoClicar} aria-busy={conferindo || undefined}>
      {children}
    </span>
  );
};
